import { BOARD_CONFIG } from "@/config/browserBoard";
import { AnnounceBanner } from "@/features/announce/components/AnnounceBanner";
import { DesktopAnnouncementContent } from "@/features/announce/components/DesktopAnnouncementContent";
import { DesktopCatalogGridLayout } from "@/features/catalog/components/desktop/DesktopCatalogGridLayout";
import { DesktopCatalogItem } from "@/features/catalog/components/desktop/DesktopCatalogItem";
import { CatalogItem } from "@/features/catalog/components/lists/CatalogItem";
import { MobileCatalogGridLayout } from "@/features/catalog/components/lists/MobileCatalogGridLayout";
import { DesktopHistorySidebar } from "@/features/history/components/DesktopHistorySidebar";
import { DesktopHistoryToolbar } from "@/features/history/components/DesktopHistoryToolbar";
import { HistoryModeToggle } from "@/features/history/components/HistoryModeToggle";
import { historyThreadKey } from "@/features/history/historyIdentity";
import {
  type HistoryPagePresentation,
  useHistoryPagePresenter,
} from "@/features/history/useHistoryPagePresenter";
import { DesktopSettingsModal } from "@/features/settings/components/desktop/DesktopSettingsModal";
import { BmgBanner } from "@/shared/ui/ad";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { LoadingScreen, Message } from "@/shared/ui/feedback";

export const HistoryPage: React.FunctionComponent = () => {
  const { node, ...view } = useHistoryPagePresenter();
  return (
    <ComponentScope node={node}>
      <HistoryPageView {...view} />
    </ComponentScope>
  );
};

export type HistoryPageViewProps = Omit<HistoryPagePresentation, "node">;

export const HistoryPageView: React.FunctionComponent<HistoryPageViewProps> = ({
  desktopHistorySidebarRef,
  mode,
  contentTitle,
  historyItemsCount,
  columns,
  imageSize,
  isDesktop,
  isSettingsOpen,
  query,
  onEvent,
}: HistoryPageViewProps): React.ReactElement => {
  const historyContent =
    historyItemsCount === 0 ? (
      <Message variant="info">{contentTitle}はありません</Message>
    ) : query.isLoading ? (
      <LoadingScreen />
    ) : query.error ? (
      <Message variant="error">{contentTitle}の読み込みに失敗しました</Message>
    ) : query.data?.length === 0 ? (
      <Message variant="info">現在存在するスレッドはありません</Message>
    ) : null;

  if (isDesktop) {
    return (
      <section
        aria-labelledby="history-page-title"
        className="desktop-catalog-page"
      >
        <DesktopHistoryToolbar
          mode={mode}
          isFetching={query.isFetching}
          onEvent={onEvent}
        />
        <h1 id="history-page-title" className="desktop-mode-title">
          履歴
        </h1>
        <DesktopAnnouncementContent>
          <section
            className="desktop-catalog-banner"
            aria-label="お知らせと広告"
          >
            <AnnounceBanner />
            <BmgBanner reserveEmptySpace priority />
          </section>
          <HistoryModeToggle
            mode={mode}
            onEvent={onEvent}
            className="desktop-history-mode-toggle"
          />
          {historyContent ?? (
            <DesktopCatalogGridLayout columns={columns} imageSize={imageSize}>
              {query.data?.map((thread) => (
                <DesktopCatalogItem
                  key={historyThreadKey({
                    boardSlug: thread.b ?? BOARD_CONFIG.boardKey,
                    threadId: thread.id,
                  })}
                  thread={thread}
                  isNew={false}
                  historyMode={mode}
                />
              ))}
            </DesktopCatalogGridLayout>
          )}
        </DesktopAnnouncementContent>
        <DesktopHistorySidebar
          ref={desktopHistorySidebarRef}
          showClosedHandle={false}
        />
        <DesktopSettingsModal
          isOpen={isSettingsOpen}
          onClose={(): void => {
            onEvent({ type: "history/settings-close-requested" });
          }}
        />
      </section>
    );
  }

  return (
    <>
      <AnnounceBanner />
      <BmgBanner reserveEmptySpace priority />
      <section aria-labelledby="history-page-title" className="history-page">
        <h1 id="history-page-title" className="sr-only">
          履歴
        </h1>
        <HistoryModeToggle mode={mode} onEvent={onEvent} />
        {historyContent ? (
          <section className="py-3" aria-label={contentTitle}>
            {historyContent}
          </section>
        ) : (
          <MobileCatalogGridLayout columns={columns}>
            {query.data?.map((thread) => (
              <CatalogItem
                key={historyThreadKey({
                  boardSlug: thread.b ?? BOARD_CONFIG.boardKey,
                  threadId: thread.id,
                })}
                thread={thread}
                isNew={false}
                historyMode={mode}
              />
            ))}
          </MobileCatalogGridLayout>
        )}
      </section>
    </>
  );
};
